import { injectable } from "inversify";
import { ApiData, ApiPage } from "@/core/models/page.model";
import { ApproveByTollAreaPayload, PaymentWithAccountantPayload, SendNotificationWaterInvoiceModel, SendSmsWaterInvoiceModel, UpdateWaterInvoiceModel, WaterInvoiceModel, WaterInvoiceSummaryModel } from "@/core/models/water-invoice.model";
import { TableRepo } from "../table.repo";
import { MultipleOpsResult } from "@/core/models/response.model";
import { WaterIndexModel } from "@/core/models/water-index.model";

interface _WaterInvoiceGetParams {
    search?: string;
}

interface VNPayQRPayload {
    data: string,
    paymentFee: number
}

export interface CreateInvoicePayload {
    waterUserId: string;
    waterIndexId?: string;
    year: string;
    month: string;
    waterMeterNumber?: number;
    waterMeterNumberLast?: number;
    readDate?: string | Date;
    readDateLast?: string | Date;
    waterFactoryId?: string;
    readerUserName?: string;
}

export interface WaterInvoiceBasic {
  id: string;
  code: number;
  year: number;
  month: number;
  total: number;
  waterUserId: string;
  waterUserName: string;
  waterUserCode: string;
  waterInvoiceStatus: string; // assuming status is either one
}


@injectable()
export class WaterInvoiceRepo extends TableRepo<WaterInvoiceModel> {
    constructor() {
        super("/api/WaterInvoice");
    }

    async getData(index: number, params: _WaterInvoiceGetParams, pageSize = 10): Promise<ApiPage<WaterInvoiceModel> | null> {
        return await super.getData(index, params, pageSize);
    }


    async getQR(model: WaterInvoiceModel): Promise<string> {
        try {
            const response = await this.apiRepo.get<string>(`${this.basepath}/get-qr`, { id: model.id });
            if (response?.code === '00') {
                return response.result
            };
        } catch (err) {
            console.log(err);
        }
        return "";
    }

    async getVNPayQR(model: WaterInvoiceModel): Promise<string> {
        try {
            const response = await this.apiRepo.get<VNPayQRPayload>(`${this.basepath}/get-vnpay-qr`, {
                id: model.id,
                returnQRImage: true,
                useLogo: true
            });
            if (response?.code === '00') {
                return "data:image/png;base64," + response.result.data
            };
        } catch (err) {
            console.log(err);
        }
        return "";
    }

    async syncInvoices(models: WaterInvoiceModel[]): Promise<MultipleOpsResult> {
        let success = 0;
        const responses = await Promise.all(models.map(model => this.apiRepo.post(`${this.basepath}/sync`, { id: model.id, approveStatus: model.approveStatus })));
        responses.forEach(response => {
            if (response?.code === '00') {
                success++;
            }
        })
        if (success > 0) {
            this.changes.next(Date.now());
        }
        return { success, total: models.length };
    }

    async syncReceipts(models: WaterInvoiceModel[]): Promise<MultipleOpsResult> {
        let success = 0;
        const responses = await Promise.all(models.map(model => this.apiRepo.post(`${this.basepath}/sync-receipt`, { id: model.id, approveStatus: model.approveStatus })));
        responses.forEach(response => {
            if (response?.code === '00') {
                success++;
            }
        })
        if (success > 0) {
            this.changes.next(Date.now());
        }
        return { success, total: models.length };
    }

    async exportExcelAll(params: object) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-excel-all`, params)
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async exportReport(params: object) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/export-report`, params)
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async SendSMSAsync(model: SendSmsWaterInvoiceModel): Promise<boolean | null> {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/send-sms`, model);
            if (response?.code === '00') {
                return true
            };
            return false;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async SendNotificationAsync(model: SendNotificationWaterInvoiceModel): Promise<boolean | null> {
        try {

            const response = await this.apiRepo.post(`${this.basepath}/send-notification`, model);
            if (response?.code === '00') {
                return true
            };
            return false;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async ExportEInvoiceAllAsync(month: string, year: string): Promise<boolean | null> {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/export-invoice-all`, {}, {month, year});
            if (response?.code === '00') {
                return true
            };
            return false;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async printInvoices(params: object) {
        try {
            const response = await this.apiRepo.getBlob(`${this.basepath}/print`, params)
            return response;
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async payWithAccountant(model: PaymentWithAccountantPayload, update = true){
        try {
            const response = await this.apiRepo.post(`${this.basepath}/payment-with-accountant`, model);
            if (response?.code === '00') {
                if (update) this.changes.next(Date.now());
                return true;
            };
            return response?.errorMessage;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async getSummary(params: object): Promise<WaterInvoiceSummaryModel[]> {
        try {
            const response = await this.apiRepo.get<ApiData<WaterInvoiceSummaryModel[]>>(`${this.basepath}/summary-by-time`, params);
            if (response?.code === '00') {
                return response.result.data
            };
        } catch (err) {
            console.log(err);
        }
        return [];
    }

    async createInvoiceWithIndex(model: WaterIndexModel) {
        try {
            const response = await this.apiRepo.post<WaterIndexModel>(`${this.basepath}/${this.addPath}`, {
                month: model.month,
                waterUserId: model.waterUserId,
                year: model.year,
                waterIndexId: model.id
            });
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true
            };
            return response?.errorMessage;
        } catch (err) {
            console.log(err);
        }
    }

    async approveByTollArea(model: ApproveByTollAreaPayload) {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/approve-by-tollarea`, model);
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            };
            return response?.errorMessage;
        } catch (err) {
            console.log(err);
            return false;
        }
    }

    async previewInvoice(model: CreateInvoicePayload) {
        try {
            const response = await this.apiRepo.post<WaterInvoiceModel>(`${this.basepath}/preview-manual`, model);
            if (response?.code === '00') {
                return response.result
            };
            return response?.errorMessage??undefined;
        } catch (err) {
            console.log(err);
            return undefined;
        }
    }

    async addInvoiceManual(model: CreateInvoicePayload) {
        try {
            const response = await this.apiRepo.post<WaterInvoiceModel>(`${this.basepath}/add-manual`, model);
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            };
            return response?.errorMessage??undefined;
        } catch (err) {
            console.log(err);
            return undefined;
        }
    }

    async addInvoice(model: CreateInvoicePayload) {
        try {
            const response = await this.apiRepo.post<WaterInvoiceModel>(`${this.basepath}/add`, model);
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            };
            return response?.errorMessage??undefined;
        } catch (err) {
            console.log(err);
            return undefined;
        }
    }

    async exportEInvoice(id: string) {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/export-temp-einvoice`, { id });
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            };
            return response?.errorMessage??undefined;
        } catch (err) {
            console.log(err);
            return undefined;
        }
    }

    async getAllBasic(params: object = {}) {
        try {
            const response = await this.apiRepo.get<ApiPage<WaterInvoiceBasic>>(`${this.basepath}/all-basic`, {
                ...params
            });
            if (response?.code === '00') {
                return response.result.data
            };
        } catch (err) {
            console.log(err);
        }
        return [];
    }

    async updateWaterInvoice(model: UpdateWaterInvoiceModel): Promise<boolean | string> {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/update`, model);
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            }
            return response?.errorMessage || "Cập nhật thất bại!";
        } catch (err) {
            console.log(err);
            return "Cập nhật thất bại!";
        }
    }

    async approveInvoice(model: UpdateWaterInvoiceModel): Promise<boolean | string> {
        try {
            const response = await this.apiRepo.post(`${this.basepath}/approve`, model);
            if (response?.code === '00') {
                this.changes.next(Date.now());
                return true;
            }
            return response?.errorMessage || "Cập nhật trạng thái duyệt thất bại!";
        } catch (err) {
            console.log(err);
            return "Cập nhật trạng thái duyệt thất bại!";
        }
    }

}